import { type MouseEvent, type ReactNode } from 'react';

import { Link } from 'react-router-dom';

import { PermissionCheck } from '@/components/permissionCheck';
import { useGame } from '@/hooks/useGame';
import { type PermissionCode } from '@/models/permission';
import { useSandboxContext } from '@/providers';

interface SandboxLinkProps {
  /** Path under the current sandbox, e.g. `items/bundle?show=<id>`. */
  to: string;
  /** What the target page requires — pass its `PagePermissions` entry so link and route gate cannot drift. */
  oneOf: PermissionCode[];
  children: ReactNode;
  /** What a viewer without the permission sees; the children by default. */
  fallback?: ReactNode;
  className?: string;
  /** `_blank` from an editable form: no navigation blocker guards its unsaved edits. */
  target?: '_blank';
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Links whatever names an entity to the page that opens it. A viewer without the
 * page's permission gets the children unwrapped instead of a click that 403s.
 *
 * Must be rendered under /:gameCode/:sandboxCode — it reads the Sandbox context.
 */
export const SandboxLink = ({
  to,
  oneOf,
  children,
  fallback = children,
  className,
  target,
  onClick,
}: SandboxLinkProps) => {
  const { studio_code, game_code } = useGame();
  const { selectedSandbox } = useSandboxContext();

  return (
    <PermissionCheck oneOf={oneOf} fallback={fallback}>
      <Link
        className={className}
        to={`/studios/${studio_code}/${game_code}/${selectedSandbox}/${to}`}
        target={target}
        rel={target ? 'noreferrer' : undefined}
        onClick={onClick}
      >
        {children}
      </Link>
    </PermissionCheck>
  );
};
